import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'

import {
  Container,
  Columns,
  Column,
  Checkbox,
  Button,
  PasswordInput,
} from '@resurs/midas-react'
import { Conditional } from '@resurs/react-utils'

import { TextInput } from '../../../../midasComponents'
import { getText } from '../../../../internal/TextLabels'
import { format } from '../../../../models/format'
import { Roles } from '../../../../models/roles'

import Page from '../../../../internal/Page'
import PageActionBar from '../../../../internal/PageActionBar'
import Datepicker from '../../../../internal/Datepicker'
import { shouldUsernameEqualEmail, validateUsername } from '../../utils'
import PermissionPicker from '../../UI/PermissionPicker'

const RequiredFields = ['username', 'firstName', 'lastName']

const AdditionalRequiredFields = ['phoneNumber', 'email']

const AddSingleUserUI = props => {
  const [indefinite, setIndefinite] = useState(true)
  const [user, setUser] = useState({
    ...props.user,
    role: props.user.role.name,
  })
  const [invalidFields, setInvalidFields] = useState([...RequiredFields])
  const [valid, setValid] = useState(false)

  useEffect(() => {
    const required =
      user.role === Roles.SUPER_USER
        ? [...RequiredFields, ...AdditionalRequiredFields]
        : [...RequiredFields]
    const empty = required.filter(field => !user[field])
    const invalid = invalidFields.filter(field => required.includes(field))
    setValid(!empty.length && !invalid.length)
  }, [invalidFields, user.role])

  const handleSave = e => {
    e.preventDefault()
    props.onSave(user)
  }

  const updateUser = (key, value) => setUser({ ...user, [key]: value })

  const handleChangePermission = ({
    role,
    additionalAuthorities,
    restrictedAuthorities,
  }) =>
    setUser({
      ...user,
      role,
      additionalAuthorities,
      restrictedAuthorities,
    })

  const validate = ({ key, valid: isValid }) => {
    const isInvalidAlready = invalidFields.some(field => field === key)
    if (isInvalidAlready && isValid) {
      setInvalidFields(invalidFields.filter(field => field !== key))
    }
    if (!isInvalidAlready && !isValid) {
      setInvalidFields([...invalidFields, key])
    }
  }

  const requiredField = () =>
    user.role === Roles.SUPER_USER ? getText('default.form.required') : ''

  return (
    <form onSubmit={handleSave}>
      <Container>
        <Columns>
          <Column col3 colXl2 colMd0 />
          <Column col6 colXl8 colMd12>
            <Page title={getText('user.add.title')}>
              <PageActionBar
                showPrev
                actions={
                  <Button
                    form
                    disabled={!valid}
                    type="primary"
                    kind="success"
                    size="small"
                  >
                    {getText('default.save')}
                  </Button>
                }
              />
              <Container>
                <Columns>
                  <Column col12>
                    <input
                      type="text"
                      id="language-code"
                      name="language-code"
                      hidden
                      defaultValue={user.languageCode}
                    />
                    <TextInput
                      id="username"
                      label={getText('user.userName')}
                      format={format.usernameOrEmail}
                      required={getText('default.form.required')}
                      value={user.username}
                      onChange={e => updateUser('username', e.target.value)}
                      isValid={validate}
                      additionalValidation={value =>
                        validateUsername(value, user.email)
                      }
                    />
                    <Conditional
                      when={
                        props.loggedInUser.role === Roles.RESURS_ADMIN ||
                        props.loggedInUser.role === Roles.SUPER_USER ||
                        props.loggedInUser.role === Roles.STORE_MANAGER
                      }
                    >
                      <PasswordInput
                        peek
                        id="password"
                        name="password"
                        autoComplete="new-password"
                        label={getText('password.activate.password')}
                        peekLabel={getText('password.peekLabel')}
                        creationLabels={getText('password.creationLabels')}
                        onChange={e => updateUser('password', e.target.value)}
                      />
                    </Conditional>
                    <TextInput
                      id="firstName"
                      label={getText('user.firstName')}
                      format={format.string}
                      required={getText('default.form.required')}
                      value={user.firstName}
                      onChange={e => updateUser('firstName', e.target.value)}
                      isValid={validate}
                    />
                    <TextInput
                      id="lastName"
                      label={getText('user.lastName')}
                      format={format.string}
                      required={getText('default.form.required')}
                      value={user.lastName}
                      onChange={e => updateUser('lastName', e.target.value)}
                      isValid={validate}
                    />
                    <TextInput
                      id="email"
                      label={getText('user.email')}
                      format={format.email}
                      value={user.email}
                      required={requiredField()}
                      onChange={e => updateUser('email', e.target.value)}
                      isValid={validate}
                      additionalValidation={value =>
                        shouldUsernameEqualEmail(user.username, value)
                          ? getText('user.add.emailMustMatchUsername')
                          : null
                      }
                    />
                    <TextInput
                      id="phoneNumber"
                      label={getText('user.phone')}
                      format={format.phone}
                      value={user.phoneNumber}
                      required={requiredField()}
                      onChange={e => updateUser('phoneNumber', e.target.value)}
                      isValid={validate}
                    />
                    <div
                      style={{
                        width: '100%',
                        display: 'inline-flex',
                        justifyContent: 'left',
                      }}
                    >
                      <Datepicker
                        name="start-date"
                        label={getText('user.startDate')}
                        selected={moment(user.startDate)}
                        onChange={date =>
                          updateUser(
                            'startDate',
                            moment(date).format('YYYY-MM-DD'),
                          )
                        }
                        maxDate={moment(user.endDate)}
                      />
                      <div style={{ paddingLeft: '1rem' }}>
                        {!indefinite && (
                          <Datepicker
                            name="end-date"
                            label={getText('user.endDate')}
                            selected={moment(user.endDate)}
                            onChange={date =>
                              updateUser(
                                'endDate',
                                moment(date).format('YYYY-MM-DD'),
                              )
                            }
                            minDate={moment(user.startDate)}
                          />
                        )}
                      </div>
                      <div style={{ padding: '2.4rem 2rem' }}>
                        <Checkbox
                          id="indefinite"
                          text={getText('user.add.noEndDate')}
                          checked={indefinite}
                          onChange={e => {
                            if (e.target.checked) {
                              updateUser(
                                'endDate',
                                moment('9999-12-31').format('YYYY-MM-DD'),
                              )
                            } else {
                              updateUser(
                                'endDate',
                                moment(user.startDate).format('YYYY-MM-DD'),
                              )
                            }
                            setIndefinite(!indefinite)
                          }}
                        />
                      </div>
                    </div>
                  </Column>
                </Columns>
              </Container>
            </Page>
            <Page title={getText('user.rolesAndAuthorities')}>
              <Container>
                <Columns>
                  <Column col12>
                    <PermissionPicker
                      validRoles={props.validRoles}
                      loggedInUser={props.loggedInUser}
                      onChange={handleChangePermission}
                    />
                  </Column>
                </Columns>
              </Container>
            </Page>
          </Column>
        </Columns>
      </Container>
    </form>
  )
}

AddSingleUserUI.propTypes = {
  user: PropTypes.object,
  loggedInUser: PropTypes.object.isRequired,
  validRoles: PropTypes.array.isRequired,
  onSave: PropTypes.func.isRequired,
}

AddSingleUserUI.defaultProps = {
  user: {},
}

export default AddSingleUserUI
